import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EMPTY_STRING, NumberUtils, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { ICashFlowTotal } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { localizationKeys } from '../../../constants/localizationKeys';
import { ICashFlowStatementTotalTableEntry } from '../../../interfaces/ICashFlowStatementTotalTableEntry.interface';
import { ICashFlowTotalComponentInput } from '../../../interfaces/ICashFlowTotalComponentInput.interface';

const tableEntriesFullRowsLength = 3;

const debug = Debug('aumico:aumico-lib-annualreport-fe:CashFlowTotalComponent');

export type TotalCaptions = {
    total1: string;
    total2: string;
};

@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-cash-flow-total-component',
    templateUrl: 'cash-flow-total.component.html',
    styleUrls: ['cash-flow-total.component.scss'],
})
export class CashFlowTotalComponent implements OnInit {
    // refer to finNumber pipe for reference on values
    @Input() numberFormat = '1.2-2';

    private totalData: ICashFlowTotalComponentInput;
    @Output() differenceInTotal = new EventEmitter<{ previousTotal: number; currentTotal: number }>();

    @Input() set total(value: ICashFlowTotalComponentInput) {
        if (value) {
            this.totalData = value;
            if (!this.editMode) {
                this.setInitialTableData();
                this.initializeForm();
            }

            if (this.updateFlisAutomatically) {
                this.updateFlisValues();
            }
        }
    }

    private savedTotalData: ICashFlowTotal;
    @Input() set savedTotal(value: ICashFlowTotal) {
        if (value) {
            this.tableHasChanges = false;
            this.editMode = true;
            this.savedTotalData = value;
            this.tableEntries = [];
            this.setSavedTableData();
            this.initializeForm();
        }
    }


    @Input() updateFlisAutomatically: boolean;

    @Input() reportLocale: string;
    @Input() set previousTotalCaptions(value: TotalCaptions) {
        if (value && this.tableEntries.length === tableEntriesFullRowsLength && !this.savedTotalData) {
            this.tableEntries[this.indexForCalculatedTotalRow].totalName = value.total2;
            this.updateForm();
        }
    }

    private formValueChanges: Subscription | undefined;

    form: UntypedFormGroup;
    displayedColumns = ['totalIndex', 'totalName', 'currentTotal', 'previousTotal'];

    private tableEntries: ICashFlowStatementTotalTableEntry[] = [];
    private local = localizationKeys;
    private editMode = false;

    tableHasChanges = false;

    indexForOpeningRow = 0;
    indexForClosingRow = 1;
    indexForCalculatedTotalRow = 2;

    dataSource: UntypedFormArray;

    constructor(private formBuilder: UntypedFormBuilder, private localizedTexts: I18NextService) {
        this.initializeForm();
    }

    ngOnInit(): void {
        this.applyLocalizations();
    }

    private applyLocalizations(): void {
        if (this.tableEntries.length === tableEntriesFullRowsLength) {
            this.applyLocalizationsOfTableEntry();
            this.updateForm();
        }
    }

    private setInitialTableData(): void {
        this.tableEntries = [];
        this.prepareTableEntries();
        this.applyLocalizationsOfTableEntry();
    }

    private applyLocalizationsOfTableEntry(): void {
        this.tableEntries[this.indexForOpeningRow].totalName = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_cashAndCashStart,
            { lng: this.reportLocale },
        ) as string;
        this.tableEntries[this.indexForClosingRow].totalName = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_cashAndCashEnd,
            { lng: this.reportLocale },
        ) as string;

        this.tableEntries[this.indexForCalculatedTotalRow].totalIndex = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_total2,
            { lng: this.reportLocale },
        ) as string;
        if (!this.editMode) {
            this.tableEntries[this.indexForCalculatedTotalRow].totalName = this.localizedTexts.t(
                this.local.aumico_annual_report_cash_flow_total_total2_name,
                { lng: this.reportLocale },
            ) as string;
        }

        this.updateForm();
    }

    private initializeForm(): void {
        this.form = this.formBuilder.group({
            dataSource: this.formBuilder.array([]),
        });
        this.dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', this.dataSource);

        // listen to form change
        this.listenToFormChange();
    }

    private updateForm(): void {
        this.dataSource = this.getAllAsFormArray();
        if (this.form) {
            this.form.setControl('dataSource', this.dataSource);
        }
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups = this.tableEntries.map((entry) => this.addFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }

    private addFormGroup(dataSource: ICashFlowStatementTotalTableEntry): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl(dataSource[key]);
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private listenToFormChange(): void {
        if (this.formValueChanges != null) {
            this.formValueChanges.unsubscribe();
        }
        this.formValueChanges = this.form.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                this.tableHasChanges = this.form.dirty;
                this.tableEntries = cloneDeep<ICashFlowStatementTotalTableEntry[]>(value.dataSource);
                this.reCalculateTotals();
            });
    }

    private prepareTableEntries(): void {
        if (this.totalData == null) {
            return;
        }

        const firstRowTotalNameLocalization = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_cashAndCashStart,
            { lng: this.reportLocale },
        ) as string;

        const secondRowTotalNameLocalization = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_cashAndCashEnd,
            { lng: this.reportLocale },
        ) as string;

        const thirdRowTotalIndexLocalization = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_total2,
            { lng: this.reportLocale },
        ) as string;
        const thirdRowTotalNameLocalization = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_total2_name,
            { lng: this.reportLocale },
        ) as string;

        if (
            firstRowTotalNameLocalization == null ||
            secondRowTotalNameLocalization == null ||
            thirdRowTotalIndexLocalization == null ||
            thirdRowTotalNameLocalization == null
        ) {
            throw new Error('One of the total localizations is null');
        }

        const totalData: ICashFlowTotalComponentInput = cloneDeep<ICashFlowTotalComponentInput>(this.totalData);
        //first row
        let openingCashAndCashEquivalents: number | null = null;
        let previousOpeningCashAndCashEquivalents: number | null = null;

        if (
            totalData.openingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.openingCashAndCashEquivalents)
        ) {
            openingCashAndCashEquivalents = NumberUtils.roundNumber(totalData.openingCashAndCashEquivalents);
        }

        if (
            totalData.previousOpeningCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.previousOpeningCashAndCashEquivalents)
        ) {
            previousOpeningCashAndCashEquivalents = NumberUtils.roundNumber(
                totalData.previousOpeningCashAndCashEquivalents,
            );
        }

        this.tableEntries.push({
            fliId: totalData.cashAndCashEquivalentsFli.name,
            totalIndex: '',
            totalName: firstRowTotalNameLocalization,
            currentTotal: openingCashAndCashEquivalents,
            previousTotal: previousOpeningCashAndCashEquivalents,
        });

        //second row
        let closingCashAndCashEquivalents: number | null = null;
        let previousClosingCashAndCashEquivalents: number | null = null;

        if (
            totalData.closingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.closingCashAndCashEquivalents)
        ) {
            closingCashAndCashEquivalents = NumberUtils.roundNumber(totalData.closingCashAndCashEquivalents);
        }

        if (
            totalData.previousClosingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.previousClosingCashAndCashEquivalents)
        ) {
            previousClosingCashAndCashEquivalents = NumberUtils.roundNumber(
                totalData.previousClosingCashAndCashEquivalents,
            );
        }

        this.tableEntries.push({
            fliId: totalData.cashAndCashEquivalentsFli.name,
            totalIndex: '',
            totalName: secondRowTotalNameLocalization,
            currentTotal: closingCashAndCashEquivalents,
            previousTotal: previousClosingCashAndCashEquivalents,
        });

        //third row
        const currentTotalOfThirdRow = NumberUtils.subtractNumbers(
            closingCashAndCashEquivalents ?? 0,
            openingCashAndCashEquivalents ?? 0,
        );

        const previousTotalOfThirdRow = NumberUtils.subtractNumbers(
            previousClosingCashAndCashEquivalents ?? 0,
            previousOpeningCashAndCashEquivalents ?? 0,
        );

        this.tableEntries.push({
            totalIndex: thirdRowTotalIndexLocalization,
            totalName: {
                [this.reportLocale]: thirdRowTotalNameLocalization,
            },
            currentTotal: currentTotalOfThirdRow,
            previousTotal: previousTotalOfThirdRow,
        });
    }

    private reCalculateTotals(): void {
        if (this.form == null) {
            return;
        }
        const dataSource = this.form.get('dataSource');
        if (dataSource == null || dataSource['controls'][this.indexForCalculatedTotalRow] == null) {
            return;
        }

        const totalControls = dataSource['controls'][this.indexForCalculatedTotalRow]['controls'];
        // current calculated Total
        const currentClosingTotal: number | null = cloneDeep<number | null>(
            this.tableEntries[this.indexForClosingRow].currentTotal,
        );

        const currentOpeningTotal: number | null = cloneDeep<number | null>(
            this.tableEntries[this.indexForOpeningRow].currentTotal,
        );

        let currentCalculatedValue: number | null = null;
        if (
            (currentClosingTotal != null && NumberUtils.isFinite(currentClosingTotal)) ||
            (currentOpeningTotal != null && NumberUtils.isFinite(currentOpeningTotal))
        ) {
            currentCalculatedValue = NumberUtils.subtractNumbers(currentClosingTotal ?? 0, currentOpeningTotal ?? 0);
        }

        totalControls['currentTotal'].setValue(currentCalculatedValue);

        // previous calculated Total
        const previousClosingTotal: number | null = cloneDeep<number | null>(
            this.tableEntries[this.indexForClosingRow].previousTotal,
        );

        const previousOpeningTotal: number | null = cloneDeep<number | null>(
            this.tableEntries[this.indexForOpeningRow].previousTotal,
        );

        let previousCalculatedValue: number | null = null;
        if (
            (previousClosingTotal != null && NumberUtils.isFinite(previousClosingTotal)) ||
            (previousOpeningTotal != null && NumberUtils.isFinite(previousOpeningTotal))
        ) {
            previousCalculatedValue = NumberUtils.subtractNumbers(previousClosingTotal ?? 0, previousOpeningTotal ?? 0);
        }

        totalControls['previousTotal'].setValue(previousCalculatedValue);

        this.differenceInTotal.emit({
            previousTotal: previousCalculatedValue ?? 0,
            currentTotal: currentCalculatedValue ?? 0,
        });
    }

    private setSavedTableData(): void {
        const firstRowTotalNameLocalization = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_cashAndCashStart,
            { lng: this.reportLocale },
        ) as string;

        const firstTotalRowIndex = EMPTY_STRING; 

        const secondRowTotalNameLocalization = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_cashAndCashEnd,
            { lng: this.reportLocale },
        ) as string;

        const thirdRowTotalIndexLocalization = this.localizedTexts.t(
            this.local.aumico_annual_report_cash_flow_total_total2,
            { lng: this.reportLocale },
        ) as string;
        if (
            firstRowTotalNameLocalization == null ||
            secondRowTotalNameLocalization == null ||
            thirdRowTotalIndexLocalization == null
        ) {
            throw new Error('One of the total localizations is null');
        }

        const totalData: ICashFlowTotal = cloneDeep(this.savedTotalData);


        //first row
        let openingCashAndCashEquivalents: number | null = null;
        if (
            totalData.openingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.openingCashAndCashEquivalents)
        ) {
            openingCashAndCashEquivalents = NumberUtils.roundNumber(totalData.openingCashAndCashEquivalents);
        }

        let previousOpeningCashAndCashEquivalents: number | null = null;
        if (
            totalData.previousOpeningCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.previousOpeningCashAndCashEquivalents)
        ) {
            previousOpeningCashAndCashEquivalents = NumberUtils.roundNumber(
                totalData.previousOpeningCashAndCashEquivalents,
            );
        }

        this.tableEntries.push({
            fliId: totalData.cashAndCashEquivalentsFliId,
            totalIndex: firstTotalRowIndex,
            totalName: firstRowTotalNameLocalization,
            currentTotal: openingCashAndCashEquivalents,
            previousTotal: previousOpeningCashAndCashEquivalents,
        });

        //second row
        let closingCashAndCashEquivalents: number | null = null;
        if (
            totalData.closingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.closingCashAndCashEquivalents)
        ) {
            closingCashAndCashEquivalents = NumberUtils.roundNumber(totalData.closingCashAndCashEquivalents);
        }

        let previousClosingCashAndCashEquivalents: number | null = null;
        if (
            totalData.previousClosingCashAndCashEquivalents != null &&
            NumberUtils.isFinite(totalData.previousClosingCashAndCashEquivalents)
        ) {
            previousClosingCashAndCashEquivalents = NumberUtils.roundNumber(
                totalData.previousClosingCashAndCashEquivalents,
            );
        }

        this.tableEntries.push({
            fliId: totalData.cashAndCashEquivalentsFliId,
            totalIndex: '',
            totalName: secondRowTotalNameLocalization,
            currentTotal: closingCashAndCashEquivalents,
            previousTotal: previousClosingCashAndCashEquivalents,
        });

        //third row
        const currentTotalOfThirdRow = NumberUtils.subtractNumbers(
            closingCashAndCashEquivalents ?? 0,
            openingCashAndCashEquivalents ?? 0,
        );

        const previousTotalOfThirdRow = NumberUtils.subtractNumbers(
            previousClosingCashAndCashEquivalents ?? 0,
            previousOpeningCashAndCashEquivalents ?? 0,
        );
        this.tableEntries.push({
            totalIndex: thirdRowTotalIndexLocalization,
            totalName: totalData.total2,
            currentTotal: currentTotalOfThirdRow,
            previousTotal: previousTotalOfThirdRow,
        });
    }

    getTotalFormValue(): ICashFlowTotal {
        const openingCashAndCashRowIndex = 0;
        const closingCashAndCashRowIndex = 1;
        const calculatedRow = 2;
        let openingCashAndCashEquivalents: number | null = null;
        let closingCashAndCashEquivalents: number | null = null;
        let previousOpeningCashAndCashEquivalents: number | null = null;
        let previousClosingCashAndCashEquivalents: number | null = null;

        if (NumberUtils.isFinite(this.form.value.dataSource[openingCashAndCashRowIndex]?.currentTotal)) {
            openingCashAndCashEquivalents = NumberUtils.roundNumber(
                this.form.value.dataSource[openingCashAndCashRowIndex].currentTotal,
            );
        }

        if (NumberUtils.isFinite(this.form.value.dataSource[closingCashAndCashRowIndex]?.currentTotal)) {
            closingCashAndCashEquivalents = NumberUtils.roundNumber(
                this.form.value.dataSource[closingCashAndCashRowIndex].currentTotal,
            );
        }

        if (NumberUtils.isFinite(this.form.value.dataSource[openingCashAndCashRowIndex]?.previousTotal)) {
            previousOpeningCashAndCashEquivalents = NumberUtils.roundNumber(
                this.form.value.dataSource[openingCashAndCashRowIndex].previousTotal,
            );
        }

        if (NumberUtils.isFinite(this.form.value.dataSource[closingCashAndCashRowIndex]?.previousTotal)) {
            previousClosingCashAndCashEquivalents = NumberUtils.roundNumber(
                this.form.value.dataSource[closingCashAndCashRowIndex].previousTotal,
            );
        }

        const totalOutput: ICashFlowTotal = {
            total1: this.savedTotalData?.total1 || '', 
            total2: this.form.value.dataSource[calculatedRow]?.totalName,
            openingCashAndCashEquivalents,
            closingCashAndCashEquivalents,
            previousOpeningCashAndCashEquivalents,
            previousClosingCashAndCashEquivalents,
            cashAndCashEquivalentsFliId: this.totalData?.cashAndCashEquivalentsFli.name,
        };
        return totalOutput;
    }

    private updateFlisValues(): void {
        // updating cash and cash equivalents fli opening and closing values
        const openingRow: ICashFlowStatementTotalTableEntry = this.tableEntries[this.indexForOpeningRow];
        const closingRow: ICashFlowStatementTotalTableEntry = this.tableEntries[this.indexForClosingRow];
        if (openingRow != null) {
            let openingCashAndCashEquivalents: number | null = null;
            if (
                this.totalData.openingCashAndCashEquivalents != null &&
                NumberUtils.isFinite(this.totalData.openingCashAndCashEquivalents)
            ) {
                openingCashAndCashEquivalents = NumberUtils.roundNumber(this.totalData.openingCashAndCashEquivalents);
            }
            openingRow.currentTotal = openingCashAndCashEquivalents;
        }

        if (closingRow != null) {
            let closingCashAndCashEquivalents: number | null = null;
            if (
                this.totalData.closingCashAndCashEquivalents != null &&
                NumberUtils.isFinite(this.totalData.closingCashAndCashEquivalents)
            ) {
                closingCashAndCashEquivalents = NumberUtils.roundNumber(this.totalData.closingCashAndCashEquivalents);
            }
            closingRow.currentTotal = closingCashAndCashEquivalents;
        }
        this.updateForm();
    }
}
